import { DateTime } from "luxon";

import Card from "../shared/Card/Card";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import useDepollutionTimers from "../../hooks/useDepollutionTimers";
import formatDateTime from "../../utils/formatDateTime";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";

interface DepollutionTimerCardProps {
  depollutionId?: string;
  /** The logged-in user, required to pause or finish a session. */
  userId?: string;
}

export default function DepollutionTimerCard({
  depollutionId,
  userId,
}: DepollutionTimerCardProps) {
  const {
    totalElapsedSeconds,
    hasActiveSession,
    firstStartedAt,
    lastActivityAt,
    isSessionActionLoading,
    handleStart,
    handlePause,
    handleFinish,
  } = useDepollutionTimers(depollutionId);

  const depollutionLabel = CheckFeatureFlag("TME") ? "Triage" : "Depollution";

  const formatDuration = (seconds: number) => {
    const safeSeconds = Math.max(0, Math.floor(seconds));
    const hours = Math.floor(safeSeconds / 3600).toString().padStart(2, "0");
    const minutes = Math.floor((safeSeconds % 3600) / 60).toString().padStart(2, "0");
    const remainingSeconds = Math.floor(safeSeconds % 60).toString().padStart(2, "0");

    return `${hours}:${minutes}:${remainingSeconds}`;
  };

  return (
    <Card title={`${depollutionLabel} Timer`} subtitle={`Start, pause and finish the ${depollutionLabel.toLowerCase()} timer. Totals include the active session.`} bodyClassName="p-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="text-left">
          <p className="font-mono text-5xl font-semibold tracking-tight text-primary-600 lg:text-6xl">
            {formatDuration(totalElapsedSeconds)}
          </p>
          <p className="mt-1 text-xs font-medium uppercase tracking-wide text-gray-500">
            {hasActiveSession ? "Session active" : "Not running"}
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            {hasActiveSession ? (
              <>
                <ButtonInput
                  label="Pause"
                  isSubmit={false}
                  classes="!bg-amber-600 hover:!bg-amber-700"
                  isLoading={isSessionActionLoading}
                  disabled={!userId}
                  onClick={() => userId && handlePause(userId)}
                />
                <ButtonInput
                  label="Finish"
                  isSubmit={false}
                  classes="!bg-green-600 hover:!bg-green-700"
                  isLoading={isSessionActionLoading}
                  disabled={!userId}
                  onClick={() => userId && handleFinish(userId)}
                />
              </>
            ) : (
              <ButtonInput
                label="Start"
                isSubmit={false}
                classes=""
                isLoading={isSessionActionLoading}
                disabled={!depollutionId}
                onClick={() => handleStart()}
              />
            )}
          </div>
        </div>

        <div className="flex w-full flex-col gap-3 lg:w-80">
          <div className="rounded-md bg-white px-3 py-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Started</p>
            <p className="text-sm text-gray-700">
              {firstStartedAt
                ? formatDateTime(firstStartedAt, DateTime.DATETIME_SHORT)
                : "-"}
            </p>
          </div>
          <div className="rounded-md bg-white px-3 py-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Last Activity</p>
            <p className="text-sm text-gray-700">
              {lastActivityAt
                ? formatDateTime(lastActivityAt, DateTime.DATETIME_SHORT)
                : "-"}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}
